import { NavigateFunction } from "react-router-dom";

import { ApiService, User } from "pwapi";

import {
  CASES_REQUEST,
  SET_LAST_CASE_VIEWED,
  WIPE_CASES,
  CASE_ID_LIST_REQUEST,
  NAVIGATE_CASE_LIST_SYNC,
  SET_CASE_SEARCH,
  NAVIGATE_CASE_LIST_END_OF_LIST,
  DOCUMENTS_LIST_REQUEST,
} from "./constants";
import {
  QueueEnum,
  CaseListPaginationParams,
  CaseListDirection,
  initialFormState,
} from "../reducers/casesReducer";
import {
  getCaseOrdering,
  getNextCaseId,
  getPrevCaseId,
} from "../utils/caseUtils";
import { reduceFiltersToApi } from "../utils/listFiltersUtils";

export const INITIAL_FETCH_LIMIT = 50;

// Formulates the parameters for the /api/cases/ request
export function extractServiceParams(
  caseListPaginationParams: CaseListPaginationParams
) {
  let serviceParams: any = { ...reduceFiltersToApi(caseListPaginationParams) };
  serviceParams["offset"] =
    (caseListPaginationParams.page - 1) *
    caseListPaginationParams.paginationPerPage;
  serviceParams["limit"] =
    caseListPaginationParams.limit ||
    caseListPaginationParams.paginationPerPage;

  if (caseListPaginationParams.sortColumn) {
    serviceParams["ordering"] = getCaseOrdering(
      caseListPaginationParams.sortDirection,
      caseListPaginationParams.sortColumn
    );
  } else {
    serviceParams["ordering"] =
      "-priority,-patient__clinic__tat_priority,received_date";
  }
  return serviceParams;
}

export const fetchCases = (
  caseListPaginationParams: CaseListPaginationParams
) => {
  return {
    type: CASES_REQUEST,
    payload: {
      caseListPaginationParams,
      serviceParams: extractServiceParams(caseListPaginationParams),
      requestTimestamp: Date.now(),
    },
  };
};

export const setLastCaseViewed = (id: number | undefined) => {
  return {
    type: SET_LAST_CASE_VIEWED,
    payload: id,
  };
};

export const fetchCaseListIds = (
  caseListPaginationParams: CaseListPaginationParams
) => {
  return {
    type: CASE_ID_LIST_REQUEST,
    payload: extractServiceParams({
      ...caseListPaginationParams,
      limit: INITIAL_FETCH_LIMIT,
      fields: ["id"],
    }),
  };
};

//TODO: This is a mass of spaghetti code. Navigation needs to be reworked for signout.
export const navigateCaseList =
  (
    navigate: NavigateFunction,
    forward = true,
    shortCircuitNavigation = false
  ) =>
  (dispatch: Function, getState: Function) => {
    const {
      cases: { caseIdList, caseIdListPending, currentCaseSearch },
      caseDetail: { id: currentCaseId },
    } = getState();

    let nextCaseId = forward
      ? getNextCaseId(currentCaseId, caseIdListPending, caseIdList)
      : getPrevCaseId(currentCaseId, caseIdListPending, caseIdList);

    const dashboardURL = caseSearchToURL(currentCaseSearch);

    return dispatch(
      nextCaseId && !shortCircuitNavigation
        ? {
            type: NAVIGATE_CASE_LIST_SYNC,
            payload: {
              id: nextCaseId,
              navigate,
            },
          }
        : {
            type: NAVIGATE_CASE_LIST_END_OF_LIST,
            payload: { navigate, dashboardURL },
          }
    );
  };

export const wipeCases = () => ({
  type: WIPE_CASES,
});

export const setCaseSearch = (caseSearchState: object) => ({
  type: SET_CASE_SEARCH,
  payload: caseSearchState,
});

export const caseSearchToURL = (
  searchState: CaseListPaginationParams,
  user?: User
) => {
  const route =
    searchState.queue === QueueEnum.Active
      ? "/active"
      : searchState.queue === QueueEnum.Working
      ? "/working"
      : searchState.queue === QueueEnum.Finalized
      ? "/finalized"
      : searchState.queue === QueueEnum.All
      ? "/all"
      : "/";
  const queryString: string[] = [];
  for (var key in searchState) {
    if ((searchState as any)[key] === false) continue;
    if ((searchState as any)[key] === "") continue;
    if ((searchState as any)[key] === undefined) continue;
    if (key === "queue") continue;
    if (key === "includeSignedOut") continue;
    if (key === "status") continue;
    if (
      (searchState as any)[key] ===
      (initialFormState(searchState.queue, user) as any)[key]
    )
      continue;
    queryString.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(
        (searchState as any)[key]
      )}`
    );
  }

  const locationSearch = queryString.filter((v) => v).length
    ? `?${queryString.join("&")}`
    : "";

  return `${route}${locationSearch}`;
};

export const fetchCaseswithDocuments = (
  params: Parameters<typeof ApiService.listCasesWithDocuments>[0] & {
    page: number;
    paginationPerPage: number;
    sortColumn?: string;
    sortDirection?: CaseListDirection;
    ordering?: string;
  }
) => {
  let serviceParams = { ...params };
  serviceParams.offset = (params.page - 1) * params.paginationPerPage || 0;
  serviceParams.limit = params.limit || params.paginationPerPage || 10;

  if (params.sortColumn) {
    serviceParams.ordering = getCaseOrdering(
      params.sortDirection,
      params.sortColumn
    );
  } else {
    serviceParams.ordering = "-priority";
  }

  serviceParams.category = params.category || "Case-Report";

  return {
    type: DOCUMENTS_LIST_REQUEST,
    payload: {
      params,
      serviceParams,
      requestTimestamp: Date.now(),
    },
  };
};
